import { useEffect } from 'react'
import { History } from 'history'
import { ResurrectionCampaign } from './useResurrectionCampaign'

/**
 * Interface for subscription info
 */
interface SubscriptionInfo {
  status: string
  currentCredit?: number
  id?: string
}

/**
 * Interface for the actions object that contains API methods
 */
interface Actions {
  reactivateSubscriptionInit: (params: any) => void
}

/**
 * Custom hook to handle subscription reactivation logic
 *
 * @param readyToRestart - Whether user is ready to restart subscription
 * @param setReadyToRestart - Function to update readyToRestart state
 * @param subscriptionInfo - User's subscription information
 * @param isResurrectionCampaignLoading - Whether campaign data is still loading
 * @param flagsReady - Whether feature flags are ready
 * @param resurrectionCampaign - The campaign data
 * @param urlParams - Parsed URL parameters
 * @param actions - Actions object containing API methods
 * @param history - History object for navigation
 * @param coupon - Optional coupon code
 * @param prepaidOfferId - Optional prepaid offer ID
 */
export interface UseReactivationParams {
  readyToRestart: boolean
  setReadyToRestart: (value: boolean) => void
  subscriptionInfo: SubscriptionInfo | undefined
  isResurrectionCampaignLoading: boolean
  flagsReady: boolean
  resurrectionCampaign: ResurrectionCampaign | undefined
  urlParams: {
    chefBrandIds: number[]
    mealIds: number[]
  }
  actions: Actions
  history: History
  coupon?: string
  prepaidOfferId?: string
  pathname?: string
}

export function useReactivation({
  readyToRestart,
  setReadyToRestart,
  subscriptionInfo,
  isResurrectionCampaignLoading,
  flagsReady,
  resurrectionCampaign,
  urlParams,
  actions,
  history,
  coupon,
  prepaidOfferId,
  pathname
}: UseReactivationParams) {
  useEffect(() => {
    // Guard clause: exit early when conditions are not met
    if (
      !readyToRestart ||
      !subscriptionInfo ||
      subscriptionInfo.status.toLowerCase() !== 'inactive' ||
      isResurrectionCampaignLoading ||
      !flagsReady
    ) {
      return
    }

    // All conditions satisfied – proceed with reactivation
    // Reset ready state to avoid duplicate calls
    setReadyToRestart(false)

    const creditsValue = subscriptionInfo?.currentCredit ?? null
    const { chefBrandIds, mealIds } = urlParams

    // DEBUG: log payload that will be sent to saga
    console.log(
      '[USE_REACTIVATION_DEBUG] Conditions satisfied, dispatching reactivateSubscriptionInit',
      {
        creditsValue,
        chefBrandIds,
        mealIds,
        resurrectionCampaign,
        coupon,
        prepaidOfferId
      }
    )

    // Initiate reactivation with all necessary parameters
    actions.reactivateSubscriptionInit({
      history,
      coupon,
      prepaidOfferId,
      creditsAvailable: creditsValue,
      resurrectionCampaign: {
        ...resurrectionCampaign,
        id: resurrectionCampaign?.id || pathname?.split('/').pop(),
        chefBrandIds: chefBrandIds || [],
        highlightedMealsIds: [
          ...mealIds,
          ...(resurrectionCampaign?.highlightedMealsIds || [])
        ],
        couponCode: resurrectionCampaign?.couponCode ?? coupon
      }
    })
  }, [
    actions,
    readyToRestart,
    setReadyToRestart,
    subscriptionInfo,
    isResurrectionCampaignLoading,
    flagsReady,
    coupon,
    prepaidOfferId,
    resurrectionCampaign,
    urlParams,
    history,
    pathname
  ])
}
